/**
 * /partners — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * ── What changed in the port ──────────────────────────────────────────────
 * The page drew its own header, footer and full-page wrapper; the layout route
 * owns those, and its <main> is already max-w-6xl with padding, so the inner
 * max-w-4xl/5xl/7xl wrappers are gone rather than nested.
 *
 * `<Reveal preset delay>` was a no-op stub from a removed motion library — it
 * rendered a bare div and ignored both props. Replaced with `bloom-rise`, the
 * project's own CSS animation, which actually moves.
 *
 * Two sets of hardcoded colours are now tokens: the grid overlay's
 * rgba(232,168,48) is the logo gold, so it uses --color-brand-mark; and the
 * closing panel's #0D1A12 on #F5E8C8 is the same primary/primary-foreground
 * pairing /pricing closes with. Hardcoded hex does not follow the theme, and
 * this brand has already changed colour once.
 */
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/shared/SEO';
import {
  ArrowRight, ChevronRight, Handshake, Coffee,
  Users, TrendingUp, Cloud, Heart,
} from 'lucide-react';

const AUDIENCES = [
  { icon: Users, title: 'Agencies', desc: 'Agencies who build software but do not want to operate it afterwards. You keep the build work; we take the running.' },
  { icon: Cloud, title: 'Consultants', desc: 'IT consultants asked "who should host this?" — and who would rather recommend someone accountable than a hosting coupon.' },
  { icon: TrendingUp, title: 'Resellers', desc: 'Resellers with existing relationships, who want recurring revenue rather than one-off project margin.' },
  { icon: Heart, title: 'Friends', desc: "You don't fit any of the above, but you know businesses that need this. Tell us." },
];

const TALKING_POINTS = [
  {
    num: '01',
    title: 'How you make money',
    desc: "Reseller margin, referral commission, revenue share, fixed fee per deal — whatever model fits your business. We're open to all of them.",
  },
  {
    num: '02',
    title: 'Who handles what',
    desc: "You sell, we deliver? You sell and deliver, we support? Joint sales calls? Let's figure out the split that uses each of our strengths.",
  },
  {
    num: '03',
    title: 'Co-marketing',
    desc: 'Logos, case studies, joint webinars, guest blog posts — anything that helps both of us reach more customers without it feeling forced.',
  },
  {
    num: '04',
    title: 'Training and support',
    desc: "You need to know enough to talk about Bloomneo confidently. We'll give you whatever it takes — calls, demos, docs, hands-on access.",
  },
  {
    num: '05',
    title: 'Exclusivity (or not)',
    desc: "Region-based exclusivity? Vertical exclusivity? No exclusivity at all? We'll work out what makes sense for your situation.",
  },
];

const PROMISES = [
  "We'll respond to every email within 48 hours",
  "You'll always talk to a real person, never a bot",
  "We'll be honest about what works and what doesn't",
  "We won't bury the deal in legal jargon",
  "We'll grow with you — small wins first, big wins later",
  'You can walk away anytime, no penalties',
];

export default function PartnersPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Partners — Refer or Resell Managed Hosting | Bloomneo"
        description="Want to be a Bloomneo reseller or partner? We're new but very open to talking. Let's grab a coffee."
        keywords="bloomneo partners, reseller program, partnership, distribution, channel partner"
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-muted/20 px-4 py-12 text-center sm:px-6 md:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(var(--color-brand-mark) 1px, transparent 1px),' +
              'linear-gradient(90deg, var(--color-brand-mark) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
          }}
        />

        <div className="relative mx-auto max-w-3xl">
          <div className="bloom-rise inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <Handshake className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
              Partner With Us
            </span>
          </div>

          <h1
            className="bloom-rise bloom-rise-1 font-bold tracking-[-0.04em] leading-[0.92] mb-6"
            style={{ fontSize: 'clamp(2.5rem, 6.5vw, 4.5rem)' }}
          >
            Want to sell<br />
            <span className="relative inline-block">
              <span className="relative z-10 text-primary">Bloomneo?</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-primary/15 -z-0 -skew-x-6" />
            </span>{' '}
            <span className="text-muted-foreground">Let's talk.</span>
          </h1>

          <p className="bloom-rise bloom-rise-2 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            We don't have a fancy partner portal yet. We don't have a 47-page reseller agreement.
            What we have is an open mind and a willingness to figure it out together.
          </p>
        </div>
      </section>

      {/* ── The honest pitch ─────────────────────────────────────────────── */}
      <section className="bloom-rise mt-16 rounded-3xl border border-border bg-card p-8 md:p-12">
        <div className="flex items-start gap-5 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <Coffee className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
              The honest pitch.
            </h2>
            <p className="text-sm text-muted-foreground">No corporate fluff. Just facts.</p>
          </div>
        </div>

        <div className="space-y-5 text-base text-muted-foreground leading-relaxed">
          <p>
            Most "partner programs" come with PDF brochures, qualification tiers, certification
            courses, and an entire team of channel managers. We have none of that yet.
          </p>
          <p>
            What we <em>do</em> have is a platform we genuinely believe in — managed cloud
            infrastructure, certificates, backups and monitoring — bundled into one managed
            subscription and operated by us.
          </p>
          <p>
            And we know we can't reach every business that needs this on our own. So if you're an
            agency, consultant, reseller, system integrator, or just someone with a network of
            businesses that could benefit — we want to talk.
          </p>
          <p className="text-foreground font-semibold pt-2">
            No pre-built program. We'll figure out something fair, together. ☕
          </p>
        </div>
      </section>

      {/* ── Who we're looking for ────────────────────────────────────────── */}
      <section className="mt-20">
        <div className="bloom-rise text-center mb-12 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
              Who We'd Love to Meet
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.03em] leading-[0.95]">
            People who get it.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AUDIENCES.map((item, i) => (
            <div
              key={item.title}
              className={`bloom-rise bloom-rise-${Math.min(i + 1, 4)} bg-muted/30 border border-border/60 rounded-2xl p-7 h-full`}
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                <item.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-bold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── What we'll figure out together ───────────────────────────────── */}
      <section className="mt-20 rounded-3xl border border-border bg-muted/20 p-6 sm:p-10">
        <div className="bloom-rise text-center mb-10">
          <div className="inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
              What We'll Figure Out
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.03em] leading-[0.95] mb-6">
            Together. Over coffee.
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            When we meet, here's what we'll talk about. None of it is set in stone — we'll shape it
            around what actually works for both sides.
          </p>
        </div>

        <div className="space-y-4">
          {TALKING_POINTS.map((item) => (
            <div
              key={item.num}
              className="bloom-rise bg-background border border-border/60 rounded-2xl p-6 md:p-7 hover:border-primary/30 transition-colors"
            >
              <div className="flex items-start gap-5">
                <div className="text-3xl font-bold text-primary/30 tracking-tighter font-mono leading-none">
                  {item.num}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── What we promise ──────────────────────────────────────────────── */}
      <section className="bloom-rise mt-20 bg-gradient-to-br from-primary/15 via-primary/5 to-primary/10 border border-primary/30 rounded-3xl p-8 md:p-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 bg-primary/15 border border-primary/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
              Our Promise
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            If you partner with us, here's the deal.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {PROMISES.map((promise) => (
            <div
              key={promise}
              className="flex items-start gap-3 bg-background/60 backdrop-blur border border-border/60 rounded-2xl px-5 py-4"
            >
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
              <span className="text-sm font-medium text-foreground">{promise}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final call ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden mt-20 mb-4 rounded-3xl bg-primary text-primary-foreground px-7 py-16 md:px-14 md:py-20 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(var(--color-brand-mark) 1px, transparent 1px),' +
              'linear-gradient(90deg, var(--color-brand-mark) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-6">
            <Coffee className="h-5 w-5" style={{ color: 'var(--color-brand-mark)' }} />
            <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--color-brand-mark)' }}>
              First step
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[0.95] mb-6">
            Send us a note.<br />
            <span style={{ color: 'var(--color-brand-mark)' }}>We'll buy the coffee.</span>
          </h2>
          <p className="opacity-80 leading-relaxed mb-10 max-w-lg mx-auto">
            Tell us a bit about who you are and how you work. We'll set up a call. No pitch deck
            required.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-stretch justify-center gap-3">
            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold text-sm shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              Reach out
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/about')}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-foreground/25 hover:bg-primary-foreground/10 font-semibold text-sm transition-all cursor-pointer"
            >
              Read about us first
              <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
